'use client'

import React from 'react'
import { SnipeShield } from '@/components/secure-listing-badge'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '@/components/ui/table'
import useWindowSize from '@/utils/hooks/useWindowSize'
import { MARKETPLACE_LABELS } from '@/utils/hooks/useCollectionGlobalSales'
import { MarketplaceIcon } from '@/components/marketplace-icon'
import { formatActivityBtc } from '@/utils/format-activity-btc'
import { formatActivityUsd } from '@/utils/format-activity-usd'
import BitcoinIcon from '@/icons/bitcoin-filled'
import Loading from '@/components/circle-loading'

import InfiniteScroll from 'react-infinite-scroll-component'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import formatNumber from '@/utils/format-number'
import { formatSatsPer } from '@/utils/format-sats'
import { formatCompactPlain } from '@/utils/format-usd'
import isFungible from '@/utils/is-fungible'
import InscriptionContent from '@/components/inscription-content'
import getRelativeTime from '@/utils/get-relative-time'
import useCollectionSalesActivity, {
	type UnifiedCollectionSale
} from '@/utils/hooks/useCollectionSalesActivity'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import { EXPLORER_URL } from '@/utils/constants'

const SHOW_MARKETPLACE_ICONS = true

//

export function Activity({
	slug,
	height,
	hideHeader = false
}: {
	slug: string
	height: string
	hideHeader?: boolean
}) {
	const { isMobile } = useWindowSize()
	const isFungibleCollection = Boolean(isFungible(slug))
	const isBrc20Collection = Boolean(slug?.toLowerCase().startsWith('brc20-'))
	const showUnitPriceSats = isFungibleCollection && !isBrc20Collection
	const headerHeight = (hideHeader ? 0 : 44) + (isFungibleCollection ? 0 : 44)
	const sellerColumnWidth = showUnitPriceSats
		? 'w-[22%]'
		: isFungibleCollection
		? 'w-[38%]'
		: 'w-[34%]'
	const totalColumnWidth = showUnitPriceSats ? 'w-[34%]' : !isFungibleCollection ? 'w-[42%]' : 'w-[50%]'
	const openInscriptionModal = useInscriptionModal()
	const {
		visibleRows,
		inscriptionsMap,
		fetchMore,
		hasMore,
		isLoading,
		isLoadingMore,
		error,
		watermark,
		freshKeys
	} = useCollectionSalesActivity(slug)
	// A sale the stream just delivered tints its row, then fades.
	const freshClass = (key: string) =>
		`transition-colors duration-700 ${freshKeys.has(key) ? 'bg-purple-500/10' : ''}`

	const { exchangeRate } = useExchangeRate()

	return (
		<div
			className={isMobile ? 'flex flex-col' : 'h-full flex flex-col'}
			style={isMobile ? undefined : { height }}
		>
			<div className="flex flex-col">
				{!hideHeader && (
					<div className="md:py-4 pb-0">
						<h1 className="text-md font-semibold px-4">Sales Activity</h1>
					</div>
				)}
				{!isFungibleCollection && (
					<Table className="w-full table-fixed">
						<TableHeader>
							<TableRow>
								<TableHead className="text-muted-foreground text-xs whitespace-nowrap text-left pl-4 pr-0 w-[12%]">
									<ClockIcon className="inline mr-1 size-4" />
								</TableHead>
								<TableHead className="text-muted-foreground text-xs whitespace-nowrap text-left pl-2 pr-0 w-[12%]">
									Item
								</TableHead>
								<TableHead
									className={`text-muted-foreground text-xs whitespace-nowrap !pl-2 !pr-0 text-left ${sellerColumnWidth}`}
								>
									Seller → Buyer
								</TableHead>
								<TableHead
									className={`text-muted-foreground text-xs whitespace-nowrap text-right pr-4 ${totalColumnWidth}`}
								>
									Total
								</TableHead>
							</TableRow>
						</TableHeader>
					</Table>
				)}
			</div>

			<div
				id="activity-scrollableDiv"
				className={isMobile ? '' : 'flex-grow overflow-y-auto'}
				style={
					isMobile
						? undefined
						: {
								height: `calc(${height} - ${headerHeight}px)`,
								maxHeight: `calc(${height} - ${headerHeight}px)`
						  }
				}
			>
				<div className="min-w-full">
					{isLoading ? (
						<div className="flex justify-center items-center h-full m-4">
							<Loading />
						</div>
					) : error ? (
						<div className="flex justify-center items-center h-full text-red-500">
							Error loading data: {(error as any).message ?? String(error)}
						</div>
					) : visibleRows.length === 0 && !watermark.canPageMore ? (
						<div className="flex justify-center items-center text-muted-foreground text-xs px-4 py-6">
							No sales recorded yet for this collection.
						</div>
					) : visibleRows.length === 0 ? (
						// Both sources have loaded their first pages but the watermark
						// hasn't cleared any rows yet (one source still has older data
						// to fetch). Show a loader so the page isn't blank.
						<div className="flex justify-center items-center h-full m-4">
							<Loading />
						</div>
					) : (
						<InfiniteScroll
							dataLength={visibleRows.length}
							next={fetchMore}
							hasMore={hasMore}
							scrollThreshold="200px"
							loader={
								isLoadingMore ? (
									<div className="flex justify-center py-3">
										<Loading />
									</div>
								) : (
									<React.Fragment />
								)
							}
							{...(isMobile ? {} : { scrollableTarget: 'activity-scrollableDiv' })}
						>
							{isFungibleCollection ? (
								<div className="divide-y divide-border/70">
									{visibleRows.map((row) => (
										<div key={row.row_key} className={freshClass(row.row_key)}>
											<CompactFungibleActivityRow
												row={row}
												exchangeRate={exchangeRate}
												showUnitPriceSats={showUnitPriceSats}
											/>
										</div>
									))}
								</div>
							) : (
								<Table className="w-full table-fixed">
									<TableBody>
										{visibleRows.map((row) => {
											const usd =
												exchangeRate && row.price_sats > 0
													? (row.price_sats / 1e8) * exchangeRate
													: null
											let perUnitSats: number | null = null
											if (showUnitPriceSats && row.amount && row.amount > 0) {
												perUnitSats = row.price_sats / row.amount
											}
											const perUnitFormatted =
												perUnitSats != null ? formatSatsPer(perUnitSats).sats : null
											const inscription = inscriptionsMap[row.inscription_id]
											// Guard against a 0/missing timestamp rendering as a 1970
											// date ("55 years ago"); show an em-dash instead.
											const tsIso =
												row.timestamp_ms > 0 ? new Date(row.timestamp_ms).toISOString() : null
											const when = tsIso ? getRelativeTime(tsIso) : '—'
											const txHref = row.txid ? `${EXPLORER_URL}/tx/${row.txid}` : undefined
											// Fungible transfer inscriptions open the modal too; it
											// shows the amount, the sale and the settlement txid.
											const canOpenModal = !!row.inscription_id
											const soldEscrow = {
												satoshi_price: row.price_sats,
												seller_address: row.seller_address || '',
												buyer_address: row.buyer_address || undefined,
												bought_at: tsIso || undefined,
												purchase_txid: row.txid || undefined,
												protected: row.protected === true
											}

											return (
												<TableRow
													key={row.row_key}
													className={`whitespace-nowrap border-0 ${
														canOpenModal ? 'cursor-pointer' : ''
													} ${freshClass(row.row_key)}`}
													onClick={
														canOpenModal
															? () =>
																	openInscriptionModal({
																		id: row.inscription_id,
																		inscription: {
																			...(inscription || {
																				id: row.inscription_id,
																				collection: { slug },
																				...(row.name ? { meta: { name: row.name } } : {})
																			}),
																			escrow: soldEscrow
																		}
																	})
															: undefined
													}
												>
													<TableCell className="py-1 text-xs text-left text-foreground pl-4 pr-0 w-[12%]">
														{txHref ? (
															<a
																href={txHref}
																target="_blank"
																rel="noreferrer"
																className="hover:underline hover:text-purple-700 dark:hover:text-purple-300"
																onClick={(e) => e.stopPropagation()}
																title={`tx ${row.txid}`}
															>
																{when}
															</a>
														) : (
															when
														)}
													</TableCell>
													{!isFungibleCollection && (
														<TableCell className="py-1 text-xs text-left pl-2 pr-0 w-[12%]">
															{row.inscription_id ? (
																<div className="size-8 rounded overflow-hidden bg-chrome-muted">
																	<InscriptionContent
																		className="size-8"
																		inscription={
																			inscription || {
																				id: row.inscription_id,
																				collection: { slug },
																				// bitmap renders its number as text; every
																				// other cache-miss / global-only id renders its
																				// real content by id (CDN, graceful onError)
																				// instead of a blank square.
																				...(slug === 'bitmap'
																					? {
																							content_type: 'text/plain',
																							meta: { name: row.name }
																					  }
																					: row.name
																					? { meta: { name: row.name } }
																					: {})
																			}
																		}
																		isDetail={false}
																		showPricePer={false}
																		smallCard={true}
																	/>
																</div>
															) : (
																<div className="size-8 rounded bg-chrome-muted" />
															)}
														</TableCell>
													)}
													<TableCell
														className={`py-1 text-xs text-foreground !pl-2 !pr-2 overflow-hidden whitespace-nowrap ${sellerColumnWidth}`}
													>
														<AddressShort address={row.seller_address} />{' '}
														<ArrowRightIcon className="inline mx-1 size-3" />{' '}
														<AddressShort address={row.buyer_address} />
													</TableCell>
													{showUnitPriceSats && (
														<TableCell className="py-1 text-xs text-left text-foreground pl-0 w-[20%] truncate overflow-hidden">
															{perUnitFormatted != null ? (
																<span>{formatNumber(perUnitFormatted)}</span>
															) : (
																'-'
															)}
														</TableCell>
													)}
													<TableCell
														className={`py-1 text-xs text-right pr-2 pl-1 ${totalColumnWidth}`}
													>
														<div className="flex min-w-0 flex-wrap items-center justify-end gap-x-1 gap-y-0.5">
															{SHOW_MARKETPLACE_ICONS && (
																<MarketplaceTag marketplace={row.marketplace} protectedSale={row.protected === true} />
															)}
															{row.inscriptions_in_tx > 1 && (
																<span
																	className="text-[9px] text-muted-foreground"
																	title={`This transaction moved ${row.inscriptions_in_tx} inscriptions. The displayed price is per item; basket prices are allocated estimates.`}
																>
																	+{row.inscriptions_in_tx - 1}
																</span>
															)}
															{row.is_mempool_pending && (
																<span className="shrink-0 inline-flex">
																	<Loading size={10} className="!w-2.5 !h-2.5" />
																</span>
															)}
															{row.price_sats > 0 ? (
																<>
																	<span className="inline-flex items-center gap-1 whitespace-nowrap"><BitcoinIcon className="shrink-0 size-3 relative -top-[0.5px]" />
																	<span>{formatActivityBtc(row.price_sats)}</span></span>
																	{usd != null && (
																		<span className="w-full text-[10px] text-muted-foreground whitespace-nowrap">
																			(${formatActivityUsd(usd)})
																		</span>
																	)}
																</>
															) : (
																<span className="text-muted-foreground">—</span>
															)}
														</div>
													</TableCell>
												</TableRow>
											)
										})}
									</TableBody>
								</Table>
							)}
						</InfiniteScroll>
					)}
				</div>
			</div>
		</div>
	)
}

function CompactFungibleActivityRow({
	row,
	exchangeRate,
	showUnitPriceSats
}: {
	row: UnifiedCollectionSale
	exchangeRate?: number | null
	showUnitPriceSats: boolean
}) {
	const usd = exchangeRate && row.price_sats > 0 ? (row.price_sats / 1e8) * exchangeRate : null
	const perUnitSats =
		showUnitPriceSats && row.amount && row.amount > 0 ? row.price_sats / row.amount : null
	const perUnitFormatted = perUnitSats != null ? formatSatsPer(perUnitSats).sats : null
	const amount = row.amount != null && Number.isFinite(row.amount) && row.amount > 0 ? row.amount : null
	const tsIso = row.timestamp_ms > 0 ? new Date(row.timestamp_ms).toISOString() : null
	const when = tsIso ? getRelativeTime(tsIso) : '—'
	const txHref = row.txid ? `${EXPLORER_URL}/tx/${row.txid}` : undefined

	return (
		<article className="grid grid-cols-[42px_minmax(0,1fr)_auto] items-start gap-2 px-3 py-2.5 transition-colors hover:bg-muted/60">
			<div className="-mr-2 flex items-center pt-0.5 text-[11px] text-muted-foreground">
				{txHref ? (
					<a
						href={txHref}
						target="_blank"
						rel="noreferrer"
						className="hover:text-foreground hover:underline"
						title={`View transaction ${row.txid}`}
					>
						{when}
					</a>
				) : (
					when
				)}
				{row.is_mempool_pending && (
					<span className="inline-flex flex-1 justify-center" role="status" aria-label="Pending confirmation">
						<Loading size={10} className="!size-2.5" />
					</span>
				)}
			</div>

			<div className="min-w-0">
				<div className="flex min-w-0 items-center gap-1 text-xs font-medium text-foreground">
					<AddressShort address={row.seller_address} />
					<ArrowRightIcon className="size-3 shrink-0 text-muted-foreground" />
					<AddressShort address={row.buyer_address} />
					{SHOW_MARKETPLACE_ICONS && (
						<span className="ml-0.5 inline-flex shrink-0">
							<MarketplaceTag marketplace={row.marketplace} protectedSale={row.protected === true} />
						</span>
					)}
				</div>
				<div className="mt-1 flex min-w-0 flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
					<span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap">
						{perUnitFormatted != null && (
							<span>{formatNumber(perUnitFormatted)} sats / token</span>
						)}
						{amount != null && (
							<span title={`${amount.toLocaleString('en-US')} tokens sold`}>
								{perUnitFormatted != null && <span aria-hidden="true">• </span>}
								{formatCompactPlain(amount)}
							</span>
						)}
					</span>
					{row.inscriptions_in_tx > 1 && (
						<span title={`This sale moved ${row.inscriptions_in_tx} inscriptions.`}>
							+{row.inscriptions_in_tx - 1} items
						</span>
					)}
				</div>
			</div>

			<div className="min-w-0 text-right">
				{row.price_sats > 0 ? (
					<>
						<div className="flex items-center justify-end gap-1 whitespace-nowrap text-xs font-semibold tabular-nums text-foreground">
							<BitcoinIcon className="size-3 shrink-0" />
							<span>{formatActivityBtc(row.price_sats)}</span>
						</div>
						{usd != null && (
							<div className="mt-1 text-[10px] tabular-nums text-muted-foreground">
								${formatActivityUsd(usd)}
							</div>
						)}
					</>
				) : (
					<span className="text-xs text-muted-foreground">—</span>
				)}
			</div>
		</article>
	)
}

/// The marketplace badge: the shared icon (every venue's logo, DotSwap
/// included, same as the homepage and activity feeds), with the snipe
/// shield ahead of it on a protected sale here.
function MarketplaceTag({ marketplace, protectedSale = false }: { marketplace: number; protectedSale?: boolean }) {
	if (!MARKETPLACE_LABELS[String(marketplace)] || marketplace === 0) return null
	return (
		<span className="inline-flex shrink-0 items-center gap-0.5">
			{marketplace === 1 && protectedSale && (
				<span title="Snipe protected · Ordinals Wallet" aria-label="Snipe protected · Ordinals Wallet" className="inline-flex shrink-0">
					<SnipeShield iconClassName="size-3" />
				</span>
			)}
			<MarketplaceIcon marketplace={marketplace} />
		</span>
	)
}

/// Compact address display: first-4…last-4 + tooltip with the full address.
/// Matches the 4…4 truncation used on the wallet profile so the same address
/// reads identically across surfaces (last-5-only was hard to recognize and
/// inconsistent). Null/empty surfaces as a muted "—" so the seller→buyer row
/// stays visually aligned even when one side is unknown.
function AddressShort({ address }: { address?: string | null }) {
	if (!address) return <span className="text-muted-foreground/60">—</span>
	return (
		<a
			href={`/address/${address}`}
			title={address}
			className="hover:underline hover:text-purple-700 dark:hover:text-purple-300"
			onClick={(e) => e.stopPropagation()}
		>
			{address.slice(-4)}
		</a>
	)
}

function ClockIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="12" cy="12" r="10" />
			<polyline points="12 6 12 12 16 14" />
		</svg>
	)
}

function ArrowRightIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<line x1="5" y1="12" x2="19" y2="12" />
			<polyline points="12 5 19 12 12 19" />
		</svg>
	)
}
